// src/pages/cadastros/Procedimentos/EditarProcedimento.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useTheme } from "../../../contexts/ThemeContext";
import { useAuth } from "../../../contexts/AuthContext";
import LayoutDesktop from "../../../layouts/LayoutDesktop";

import { db } from "../../../config/firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  updateDoc,
  where,
  serverTimestamp,
} from "firebase/firestore";

import { uploadToStorage } from "../../../utils/storageUpload";

function EditarProcedimento() {
  const { tema } = useTheme();
  const { usuario: usuarioLogado } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams(); // id do procedimento

  const [maquinas, setMaquinas] = useState([]);
  const [setores, setSetores] = useState([]);
  const [setorIdSelecionado, setSetorIdSelecionado] = useState("");
  const [carregandoListas, setCarregandoListas] = useState(false);

  const [codigo, setCodigo] = useState("");
  const [maquinasSelecionadas, setMaquinasSelecionadas] = useState([]);
  const [descricao, setDescricao] = useState("");
  const [fotosTexto, setFotosTexto] = useState("");
  const [arquivosTexto, setArquivosTexto] = useState("");
  const [observacoes, setObservacoes] = useState("");

  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");

  const [enviandoFotos, setEnviandoFotos] = useState(false);
  const inputFotoCameraRef = useRef(null);
  const inputFotoArquivoRef = useRef(null);

  const fotosPreview = useMemo(() => {
    return (fotosTexto || "")
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean);
  }, [fotosTexto]);

  async function handleEnviarFotos(fileList) {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) return;

    setErro("");
    setSucesso("");

    const grupoId = usuarioLogado?.grupoId;
    if (!grupoId) {
      setErro(
        "Usuário logado não possui grupo definido. Verifique o cadastro do Gestor."
      );
      return;
    }

    setEnviandoFotos(true);
    try {
      const empresaId = usuarioLogado?.empresaId || null;
      const novasUrls = [];

      for (const file of files) {
        const url = await uploadToStorage({
          grupoId,
          empresaId,
          pasta: "procedimentos/fotos",
          file,
          filenamePrefix: "procedimento_foto",
        });
        novasUrls.push(url);
      }

      setFotosTexto((prev) => {
        const linhas = String(prev || "")
          .split("\n")
          .map((l) => l.trim())
          .filter(Boolean);
        for (const url of novasUrls) {
          if (!linhas.includes(url)) linhas.push(url);
        }
        return linhas.join("\n");
      });
    } catch (err) {
      console.error(err);
      setErro("Erro ao enviar foto. Verifique o Firebase Storage e tente novamente.");
    } finally {
      setEnviandoFotos(false);
      if (inputFotoCameraRef.current) inputFotoCameraRef.current.value = "";
      if (inputFotoArquivoRef.current) inputFotoArquivoRef.current.value = "";
    }
  }

  function removerFoto(url) {
    const u = String(url || "").trim();
    if (!u) return;
    setFotosTexto((prev) => {
      const linhas = String(prev || "")
        .split("\n")
        .map((l) => l.trim())
        .filter(Boolean)
        .filter((x) => x !== u);
      return linhas.join("\n");
    });
  }

  useEffect(() => {
    async function carregarMaquinas() {
      if (!usuarioLogado?.grupoId) return;
      setCarregandoListas(true);

      const grupoId = usuarioLogado.grupoId;
      const empresaId = usuarioLogado.empresaId || null;

      try {
        const maqRef = collection(db, "maquinas");
        let qM;
        if (empresaId) {
          qM = query(
            maqRef,
            where("grupoId", "==", grupoId),
            where("empresaId", "==", empresaId)
          );
        } else {
          qM = query(maqRef, where("grupoId", "==", grupoId));
        }
        const snapM = await getDocs(qM);
        const listaM = snapM.docs.map((d) => ({ id: d.id, ...d.data() }));

        listaM.sort((a, b) =>
          String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR", {
            sensitivity: "base",
          })
        );

        setMaquinas(listaM);
        } catch (e) {
        console.error("Erro ao carregar máquinas:", e);
        setErro((prev) =>
          prev
            ? prev + " / Erro ao carregar máquinas."
            : "Erro ao carregar máquinas."
        );
      } finally {
        setCarregandoListas(false);
      }
    }

    carregarMaquinas();
  }, [usuarioLogado]);

  useEffect(() => {
    async function carregarSetores() {
      if (!usuarioLogado?.grupoId) return;

      setCarregandoListas(true);
      const grupoId = usuarioLogado.grupoId;
      const empresaId = usuarioLogado.empresaId || null;

      try {
        const setoresRef = collection(db, "setores");
        let qS;

        if (empresaId) {
          qS = query(
            setoresRef,
            where("grupoId", "==", grupoId),
            where("empresaId", "==", empresaId)
          );
        } else {
          qS = query(setoresRef, where("grupoId", "==", grupoId));
        }

        const snapS = await getDocs(qS);
        const listaS = snapS.docs.map((d) => ({ id: d.id, ...d.data() }));

        listaS.sort((a, b) =>
          String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR", {
            sensitivity: "base",
          })
        );

        setSetores(listaS);
      } catch (e) {
        console.error("Erro ao carregar setores:", e);
        setErro((prev) =>
          prev ? prev + " / Erro ao carregar setores." : "Erro ao carregar setores."
        );
      } finally {
        setCarregandoListas(false);
      }
    }

    carregarSetores();
  }, [usuarioLogado]);



  useEffect(() => {
    async function carregarProcedimento() {
      if (!id) return;

      try {
        setCarregando(true);
        const ref = doc(db, "procedimentos", id);
        const snap = await getDoc(ref);

        if (!snap.exists()) {
          setErro("Procedimento não encontrado.");
          setCarregando(false);
          return;
        }

        const dados = snap.data();

        setCodigo(dados.codigo || "");
        setMaquinasSelecionadas(dados.maquinasIds || []);
        setDescricao(dados.descricao || "");

        if (Array.isArray(dados.fotos)) {
          setFotosTexto(dados.fotos.join("\n"));
        } else if (typeof dados.fotos === "string") {
          setFotosTexto(dados.fotos);
        }

        if (Array.isArray(dados.arquivos)) {
          setArquivosTexto(dados.arquivos.join("\n"));
        } else if (typeof dados.arquivos === "string") {
          setArquivosTexto(dados.arquivos);
        }

        setObservacoes(dados.observacoes || "");
      } catch (e) {
        console.error(e);
        setErro("Erro ao carregar procedimento.");
      } finally {
        setCarregando(false);
      }
    }

    carregarProcedimento();
  }, [id]);

  function handleChangeSetor(e) {
    const novoId = e.target.value;
    setSetorIdSelecionado(novoId);
    setMaquinasSelecionadas([]);
  }

  function handleChangeMaquinas(e) {
    const options = Array.from(e.target.options);
    const selecionadas = options
      .filter((opt) => opt.selected)
      .map((opt) => opt.value);
    setMaquinasSelecionadas(selecionadas);
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErro("");
    setSucesso("");

    if (maquinasSelecionadas.length === 0) {
      setErro("Selecione pelo menos uma máquina.");
      return;
    }

    if (!descricao.trim()) {
      setErro("Informe a descrição do procedimento.");
      return;
    }

    const fotosArray = fotosTexto
      .split("\n")
      .map((l) => l.trim())
      .filter((l) => l.length > 0);

    const arquivosArray = arquivosTexto
      .split("\n")
      .map((l) => l.trim())
      .filter((l) => l.length > 0);

    const maquinasNomes = maquinasSelecionadas
      .map((id) => maquinas.find((m) => m.id === id)?.nome || "")
      .filter((n) => n);

    try {
      setSalvando(true);

      const setorSelecionado = setores.find((s) => s.id === setorIdSelecionado) || null;

      const ref = doc(db, "procedimentos", id);
      await updateDoc(ref, {
        grupoId: usuarioLogado?.grupoId || null,
        grupoNome: usuarioLogado?.grupoNome || usuarioLogado?.grupo || null,
        maquinasIds: maquinasSelecionadas,
        maquinasNomes,
        descricao: descricao.trim(),
        fotos: fotosArray.length ? fotosArray : [],
        arquivos: arquivosArray.length ? arquivosArray : [],
        observacoes: observacoes.trim() || null,
        updatedAt: serverTimestamp(),
      });

      setSucesso("Procedimento atualizado com sucesso!");
    } catch (e) {
      console.error(e);
      setErro("Erro ao salvar alterações.");
    } finally {
      setSalvando(false);
    }
  };

  if (carregando) {
    const maquinasFiltradas = setorIdSelecionado
    ? maquinas.filter((m) => m.setorId === setorIdSelecionado)
    : maquinas;

  return (
      <LayoutDesktop tema={tema} titulo="Editar Procedimento" subtitulo="">
        <p>Carregando dados do procedimento...</p>
      </LayoutDesktop>
    );
  }

  return (
    <LayoutDesktop
      tema={tema}
      titulo="Editar Procedimento"
      subtitulo={codigo ? `Ajuste de dados do procedimento ${codigo}` : ""}
    >
      <section className="pc-card">
        <h2 style={{ marginTop: 0 }}>Dados do procedimento</h2>

        {erro && (
          <div
            style={{
              marginTop: 12,
              padding: 8,
              borderRadius: 8,
              background: "#7f1d1d",
              color: "#fecaca",
              fontSize: 14,
            }}
          >
            {erro}
          </div>
        )}

        {sucesso && (
          <div
            style={{
              marginTop: 12,
              padding: 8,
              borderRadius: 8,
              background: "#14532d",
              color: "#bbf7d0",
              fontSize: 14,
            }}
          >
            {sucesso}
          </div>
        )}

        <form
          onSubmit={handleSubmit}
          style={{
            marginTop: 12,
            maxWidth: 720,
            marginLeft: "auto",
            marginRight: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 12,
          }}
        >
          {/* Código */}
          <label>
            <span style={{ fontSize: 14 }}>Código</span>
            <input
              type="text"
              value={codigo || ""}
              readOnly
              style={{
                width: "100%",
                padding: "9px 11px",
                marginTop: 4,
                borderRadius: 8,
                border: "1px solid #334155",
                background: "#02061733",
                color: "inherit",
              }}
            />
          </label>

          {/* Máquinas */}
          <label>
            <span style={{ fontSize: 14 }}>Máquinas</span>
            <select
              multiple
              value={maquinasSelecionadas}
              onChange={handleChangeMaquinas}
              disabled={carregandoListas}
              style={{
                width: "100%",
                padding: "9px 11px",
                marginTop: 4,
                borderRadius: 8,
                border: "1px solid #334155",
                
                color: "inherit",
                minHeight: 120,
              }}
            >
              {carregandoListas ? (
                <option>Carregando máquinas...</option>
              ) : maquinas.length === 0 ? (
                <option>Não há máquinas cadastradas.</option>
              ) : (
                maquinas.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.nome || "(sem nome)"}
                  </option>
                ))
              )}
            </select>
            <small style={{ fontSize: 12, opacity: 0.8 }}>
              Segure CTRL (ou CMD) para selecionar várias.
            </small>
          </label>

          {/* Descrição */}
          <label>
            <span style={{ fontSize: 14 }}>Descrição</span>
            <textarea
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              rows={4}
              style={{
                width: "100%",
                padding: "9px 11px",
                marginTop: 4,
                borderRadius: 8,
                border: "1px solid #334155",
                
                resize: "vertical",
              }}
            />
          </label>

          {/* Fotos */}
          <label>
            <span style={{ fontSize: 14 }}>Fotos</span>

            <div
              style={{
                marginTop: 6,
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <button
                type="button"
                className="pc-btn-secundario"
                disabled={enviandoFotos}
                onClick={() => inputFotoCameraRef.current?.click()}
              >
                📷 Tirar foto
              </button>

              <button
                type="button"
                className="pc-btn-secundario"
                disabled={enviandoFotos}
                onClick={() => inputFotoArquivoRef.current?.click()}
              >
                ⬆️ Carregar foto
              </button>

              {enviandoFotos ? (
                <small style={{ fontSize: 12, opacity: 0.85 }}>
                  Enviando...
                </small>
              ) : null}
            </div>

            {/* Inputs escondidos */}
            <input
              ref={inputFotoCameraRef}
              type="file"
              accept="image/*"
              capture="environment"
              multiple
              style={{ display: "none" }}
              onChange={(e) => handleEnviarFotos(e.target.files)}
            />
            <input
              ref={inputFotoArquivoRef}
              type="file"
              accept="image/*"
              multiple
              style={{ display: "none" }}
              onChange={(e) => handleEnviarFotos(e.target.files)}
            />

            {/* Preview */}
            {fotosPreview.length > 0 ? (
              <div
                style={{
                  marginTop: 10,
                  display: "flex",
                  gap: 10,
                  flexWrap: "wrap",
                }}
              >
                {fotosPreview.map((url) => (
                  <div
                    key={url}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: 8,
                      borderRadius: 10,
                      border: "1px solid #334155",
                    }}
                  >
                    <img
                      src={url}
                      alt=""
                      style={{
                        width: 52,
                        height: 52,
                        objectFit: "cover",
                        borderRadius: 8,
                        border: "1px solid #334155",
                      }}
                    />
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 6,
                      }}
                    >
                      <a
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        style={{ fontSize: 12, opacity: 0.9 }}
                      >
                        Abrir
                      </a>
                      <button
                        type="button"
                        className="pc-btn-perigo"
                        style={{ padding: "4px 8px", fontSize: 12 }}
                        onClick={() => removerFoto(url)}
                      >
                        Remover
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : null}

            <div style={{ marginTop: 10 }}>
              <small style={{ fontSize: 12, opacity: 0.85 }}>
                (Opcional) Se preferir, você pode colar URLs abaixo (uma por linha):
              </small>
            </div>

            <textarea
              value={fotosTexto}
              onChange={(e) => setFotosTexto(e.target.value)}
              rows={3}
              style={{
                width: "100%",
                padding: "9px 11px",
                marginTop: 4,
                borderRadius: 8,
                border: "1px solid #334155",
                
                resize: "vertical",
              }}
            />
          </label>

          {/* Arquivos */}
          <label>
            <span style={{ fontSize: 14 }}>
              Arquivos (URLs, uma por linha)
            </span>
            <textarea
              value={arquivosTexto}
              onChange={(e) => setArquivosTexto(e.target.value)}
              rows={3}
              style={{
                width: "100%",
                padding: "9px 11px",
                marginTop: 4,
                borderRadius: 8,
                border: "1px solid #334155",
                
                resize: "vertical",
              }}
            />
          </label>

          {/* Observações */}
          <label>
            <span style={{ fontSize: 14 }}>Observações</span>
            <textarea
              value={observacoes}
              onChange={(e) => setObservacoes(e.target.value)}
              rows={3}
              style={{
                width: "100%",
                padding: "9px 11px",
                marginTop: 4,
                borderRadius: 8,
                border: "1px solid #334155",
                
                resize: "vertical",
              }}
            />
          </label>

          <div
            style={{
              marginTop: 14,
              display: "flex",
              gap: 10,
              justifyContent: "flex-end",
            }}
          >
            <button
              type="button"
              onClick={() => navigate("/cadastros/procedimentos")}
              className="pc-btn-secundario"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="pc-btn-primario"
              disabled={salvando}
            >
              {salvando ? "Salvando..." : "Salvar alterações"}
            </button>
          </div>
        </form>
      </section>
    </LayoutDesktop>
  );
}

export default EditarProcedimento;
